import { useEffect, useRef } from 'react';

/**
 * A hook that handles focus trapping within a modal or dropdown menu
 * This ensures keyboard users can tab through a modal without escaping it
 * and focus returns to the trigger element when closed
 */
export function useFocusTrap(
  isOpen: boolean,
  containerRef: React.RefObject<HTMLElement>,
  triggerRef: React.RefObject<HTMLElement>
): void {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element to restore later
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      // Focus the first focusable element in the container when opened
      const focusableElements = containerRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements?.length) {
        // Wait a moment for the animation to start
        setTimeout(() => {
          if (focusableElements[0]) {
            focusableElements[0].focus();
          }
        }, 100);
      }
    } else if (previousFocusRef.current && triggerRef.current) {
      // Return focus to the trigger button when closing
      setTimeout(() => {
        triggerRef.current?.focus();
      }, 100);
    }
    
    return () => {
      // When component unmounts, ensure focus is restored
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen, containerRef, triggerRef]);
  
  // Handle key presses
  useEffect(() => {
    if (!isOpen) return undefined;
    
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Close on escape key
      if (e.key === 'Escape') {
        if (triggerRef.current) {
          triggerRef.current.click();
        }
        return;
      }
      
      // Only trap focus if Tab is pressed
      if (e.key !== 'Tab') return;
      
      const focusableElements = containerRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusableElements?.length) return;
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (!firstElement || !lastElement) return;
      
      // If shift + tab on first element, move to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // If tab on last element, move to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, containerRef, triggerRef]);
}

